"use client"

import { useState, useEffect, useRef } from "react"
import { isNativeApp, isWebOnlyPath } from "@/lib/native"
import { LocaleLink as Link } from "@/components/locale-link"
import { usePathname } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import type { AuthChangeEvent, Session } from "@supabase/supabase-js"
import { MapPin, BookOpen, LogOut, LogIn, LayoutDashboard, UserPlus, DollarSign, Globe, X, Briefcase, Users, Search } from "lucide-react"
import { useTranslations } from "next-intl"
import { InstallAppBanner } from "@/components/install-app-banner"
import { LocaleSwitcher } from "@/components/locale-switcher"
import { isMarketingContentPath, isPublicBookingSurface } from "@/i18n/routing"

// Audience-mode key — mirrors LANDING_MODE_STORAGE_KEY in app/page.tsx
// so the navbar's primary link can swap (Barber → Browse Shops vs.
// Owner → Find Barbers) in sync with the landing toggle. The toggle
// dispatches a "booth:landing-mode-change" CustomEvent so same-tab
// updates land instantly; cross-tab updates come via the native
// "storage" event.
const LANDING_MODE_KEY = "boothLandingMode"
type LandingAudience = "barber" | "owner"

const MoreHorizontalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="2.5" />
    <circle cx="20" cy="12" r="2.5" />
    <circle cx="4" cy="12" r="2.5" />
  </svg>
)

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

export function PublicNavbar() {
  const t = useTranslations("marketing.nav")
  const pathname = usePathname()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // Effect, not inline: isNativeApp() is false in SSR and true in the shell,
  // so reading it during render would mismatch on hydration.
  const [native, setNative] = useState(false)
  useEffect(() => setNative(isNativeApp()), [])
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [userRole, setUserRole] = useState<"barber" | "owner">("barber")
  const [audience, setAudience] = useState<LandingAudience>("owner")

  // Floating treatment is landing-only. Other public pages keep the
  // existing solid white navbar so their content layout doesn't shift.
  // The Spanish landing lives at /es (route-based marketing locale), so it
  // gets the same treatment as / — normalize the prefix so every route
  // check below holds for both locales.
  const normalizedPath = pathname === "/es" ? "/" : (pathname?.replace(/^\/es\//, "/") || "/")
  const isLanding = normalizedPath === "/"

  // Theme-token surfaces — pages whose root is `bg-background`, so they follow
  // the app's dark mode: the public booking funnel (/book/*) and discovery
  // (/barbers, /listings). On these the hardcoded white header read as a
  // glaring white bar over a dark page (reported on /book/[slug]) — the nav
  // must follow the same tokens as the page. Marketing/legal pages hardcode
  // light content, so they keep the solid white bar even in app dark mode.
  const isTokenSurface = ["/book", "/barbers", "/listings"].some(
    (p) => normalizedPath === p || normalizedPath.startsWith(`${p}/`),
  )

  // /listings is barber territory — someone reading a shop listing is a
  // barber sizing up a chair, not an owner. But `audience` defaults to
  // "owner" and only moves via the landing page's toggle, which never runs
  // here, so a logged-out visitor got stuck seeing "Find Barbers" — an
  // owner action — on a page that has nothing to do with hiring. Swap the
  // primary slot for an Articles link instead of guessing at an audience.
  const isListingsSurface = normalizedPath === "/listings" || normalizedPath.startsWith("/listings/")

  // The barber booking links (/book/[slug] + /book/quick/[slug]) carry their own
  // generous top padding (pt-16/24 mobile, pt-28 desktop) and clear the fixed
  // bar themselves, so we drop the injected `body { padding-top }` below to avoid
  // a double gap. (This is purely a spacing concern — navbar COLORS follow the
  // theme via `isTokenSurface`, which already covers /book/*.) /book/shops/* keep
  // the standard body padding, so they're excluded.
  const isBookingFunnelSelfPadded =
    normalizedPath === "/book" ||
    (normalizedPath.startsWith("/book/") && !normalizedPath.startsWith("/book/shops"))

  // Auto-hide-on-scroll-down + show-on-scroll-up. Only active when
  // floating, so non-landing pages keep their static header.
  const [isHidden, setIsHidden] = useState(false)
  const lastScrollY = useRef(0)

  // Theme is observed from sections marked with data-nav-theme. Default
  // to "dark" since the landing's first section is the hero (dark image).
  const [theme, setTheme] = useState<"light" | "dark">("dark")

  useEffect(() => {
    if (!isLanding) return
    const onScroll = () => {
      const y = window.scrollY
      if (y < 8) {
        setIsHidden(false)
      } else {
        const delta = y - lastScrollY.current
        if (delta > 6) setIsHidden(true)
        else if (delta < -6) setIsHidden(false)
      }
      lastScrollY.current = y
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener("scroll", onScroll)
  }, [isLanding])

  useEffect(() => {
    if (!isLanding) return
    // Pick the section currently behind the navbar (top edge ~28px) and
    // adopt its declared theme. Re-evaluates on scroll + intersection.
    const NAV_ANCHOR = 28
    const evaluate = () => {
      const sections = document.querySelectorAll<HTMLElement>("[data-nav-theme]")
      let active: "light" | "dark" | null = null
      sections.forEach((el) => {
        const r = el.getBoundingClientRect()
        if (r.top <= NAV_ANCHOR && r.bottom > NAV_ANCHOR) {
          const t = el.dataset.navTheme
          if (t === "dark" || t === "light") active = t
        }
      })
      if (active) setTheme(active)
    }
    evaluate()
    window.addEventListener("scroll", evaluate, { passive: true })
    window.addEventListener("resize", evaluate)
    // Run once after the next paint in case sections mount after this effect.
    const raf = requestAnimationFrame(evaluate)
    return () => {
      window.removeEventListener("scroll", evaluate)
      window.removeEventListener("resize", evaluate)
      cancelAnimationFrame(raf)
    }
  }, [isLanding, pathname])

  useEffect(() => {
    const supabase = createClient()

    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setIsLoggedIn(!!user)

      if (user) {
        const { data: profile } = await supabase.from("profiles").select("role").eq("id", user.id).single()

        if (profile?.role) {
          setUserRole(profile.role as "barber" | "owner")
        }
      }

      setIsLoading(false)
    }

    checkAuth()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      setIsLoggedIn(!!session?.user)
      if (session?.user) {
        // Never call supabase.* synchronously inside this callback — it fires
        // while auth-js still holds its navigator.locks mutex, and a nested
        // auth call deadlocks the sign-in that triggered the event (endless
        // "Signing in…" spinner). setTimeout defers past the lock.
        setTimeout(() => checkAuth(), 0)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleSignOut = async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
    setIsMenuOpen(false)
    window.location.href = "/"
  }

  // Sync audience with the landing-page toggle (custom event + cross-tab
  // storage). Default is "owner" and we do NOT restore a saved choice on
  // mount — the landing always opens Owner-first, so the navbar matches.
  // In-session toggles (same tab via event, other tabs via storage) still
  // keep the primary action ("Browse Shops" ↔ "Find Barbers") in lockstep.
  useEffect(() => {
    const read = () => {
      try {
        const v = window.localStorage.getItem(LANDING_MODE_KEY)
        if (v === "barber" || v === "owner") setAudience(v)
      } catch {}
    }
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<LandingAudience>).detail
      if (detail === "barber" || detail === "owner") setAudience(detail)
    }
    window.addEventListener("storage", read)
    window.addEventListener("booth:landing-mode-change", onCustom as EventListener)
    return () => {
      window.removeEventListener("storage", read)
      window.removeEventListener("booth:landing-mode-change", onCustom as EventListener)
    }
  }, [])

  // Primary nav link — swaps based on the audience toggle so each
  // mode's first action mirrors what they're looking for:
  //   • Barber mode → Browse Shops (find a chair to work)
  //   • Owner mode  → Find Barbers (find a barber to fill a chair)
  // Logged-in users routed to the role-specific community feed
  // (their talent feed), which is the live barber/owner network UI.
  // Signed in, the ROLE decides — not the landing toggle.
  //
  // `audience` defaults to "owner" and is only moved by the landing's own
  // switch, which is right for a logged-out visitor meeting the product for the
  // first time. It became wrong the moment this navbar started rendering for
  // signed-in users on marketing pages: a barber got "Find Barbers", an owner
  // action, on a nav sitting above their own barber dashboard. We know who they
  // are, and a known role beats a marketing default every time.
  const isOwnerAudience = isLoggedIn ? userRole === "owner" : audience === "owner"
  const findBarbersHref = isLoggedIn
    ? userRole === "owner" ? "/dashboard/owner/network" : "/dashboard/barber/network"
    : "/barbers"
  const primaryLink =
    // The Articles swap is a web-only flourish: on /listings the primary slot
    // points at the journal. In the app that would make a marketing page the
    // most prominent thing in the nav, so it keeps Browse Shops instead.
    !isLoggedIn && isListingsSurface && !native
      ? { icon: BookOpen, label: t("articles"), href: "/blog" }
      : isOwnerAudience
        ? { icon: Users, label: t("findBarbers"), href: findBarbersHref }
        : { icon: MapPin, label: t("browseShops"), href: "/listings" }

  // PRD §11: logged-out visitors on a public BOOKING surface (barber
  // profile, /book/*, shop pages) are booking clients, not pro prospects —
  // the pro-marketing menu (Browse Shops / Job Board / Blog / pro Pricing)
  // doesn't belong there. They get client Pricing + Sign Up (client flow).
  const bookingSurface = !isLoggedIn && isPublicBookingSurface(pathname ?? "")
  const signupHref = bookingSurface ? "/auth/signup/client" : "/auth/signup"

  // Rewards intentionally removed from the modal — surfaced via footer
  // links instead. The Sign Up CTA takes the freed bottom-right slot.
  // First tile mirrors the primary nav link (audience-aware).
  const publicMenuItems = bookingSurface
    ? [{ icon: DollarSign, label: t("pricing"), href: "/pricing?audience=client" }]
    : [
        // On listings, primaryLink IS the Articles tile already covered by
        // the Blog entry below — skip it here so it isn't shown twice.
        ...(!isLoggedIn && isListingsSurface ? [] : [primaryLink]),
        // Community is in-app only — surfaced to signed-in users, hidden from
        // the logged-out public menu.
        ...(isLoggedIn ? [{ icon: Globe, label: t("community"), href: "/community" }] : []),
        { icon: Search, label: t("search"), href: "/search" },
        { icon: Briefcase, label: t("jobBoard"), href: "/hiring" },
        { icon: DollarSign, label: t("pricing"), href: "/pricing" },
        { icon: BookOpen, label: t("blog"), href: "/blog" },
      ].filter((item) => !native || !isWebOnlyPath(item.href))

  const dashboardUrl = userRole === "owner" ? "/dashboard/owner" : "/dashboard/barber"

  // Self-suppress on authenticated tablet/desktop — the global FloatingNav
  // replaces this navbar everywhere except the landing page. Landing
  // (/) keeps PublicNavbar for everyone (auth or not) so the marketing
  // surface reads consistently. Mobile is unaffected since this
  // header is already 'hidden md:block'.
  // Render nothing while auth state is still being determined — prevents
  // the brief flash of PublicNavbar for signed-in users on non-landing
  // routes that resolve to "hidden" once isLoading flips false.
  if (isLoading && !isLanding) return null
  // Signed in, the marketing navbar gives way to the app chrome — EXCEPT on the
  // marketing content pages, which a signed-in visitor still reads as marketing
  // and which have no other way back to Pricing / Blog / the rest of the site.
  // /traction was the clearest break: it dropped its bespoke header in favour of
  // this navbar, so signed in it rendered no header at all.
  if (isLoggedIn && !isLanding && !isMarketingContentPath(pathname)) return null

  // Theme tokens — dark inversion applies on landing (section-driven), and
  // token surfaces get dark: variants so the chrome follows app dark mode
  // along with the page. Marketing pages keep the original light-only text.
  const isDark = isLanding && theme === "dark"
  const linkClasses = isDark
    ? "text-white/90 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
    : isTokenSurface
      ? "text-gray-600 hover:text-gray-900 dark:text-neutral-400 dark:hover:text-white"
      : "text-gray-600 hover:text-gray-900"
  const ctaClasses = isDark
    ? "bg-white text-black hover:bg-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.18)]"
    : isTokenSurface
      ? "bg-black text-white hover:bg-zinc-900 dark:bg-white dark:text-black dark:hover:bg-gray-100"
      : "bg-black text-white hover:bg-zinc-900"
  const iconColor = isDark
    ? "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
    : isTokenSurface
      ? "text-gray-900 dark:text-white"
      : "text-gray-900"

  return (
    <>
      {/* Mobile landing top bar — clean white container with the BOOTH
          wordmark on the left and the auth CTA on the right. Slides up
          out of view on scroll-down and back in on scroll-up, sharing
          the same isHidden state as the desktop landing nav. Landing-only
          (isLanding) so other mobile public pages keep the centered
          MobileLogoHeader wordmark; on '/' that centered wordmark is
          suppressed to avoid doubling up. Logo size left untouched. */}
      {isLanding && (
        <header
          className={`md:hidden fixed top-0 inset-x-0 z-50 bg-white border-b border-gray-200 transition-transform duration-300 ease-out ${
            isHidden ? "-translate-y-full" : "translate-y-0"
          }`}
        >
          {/* PWA install banner — white strip above the nav row, reads as
              one seamless block. Self-hides when installed/dismissed and
              announces visibility so LandingModeToggle can shift down. */}
          <InstallAppBanner />
          <div className="relative flex h-14 items-center justify-between px-4">
            {/* Audience-aware left action — speaks to whoever walked
                through the door (mirrors the primaryLink swap below):
                  • Owner mode → Book a demo (founder-led sales pipeline;
                    same Cal.com link as the footer)
                  • Barber mode → Jobs (highest-intent barber action that
                    isn't Sign Up — they come for the job, stay for the
                    booth)
                Quiet text so the Sign Up pill keeps visual priority. */}
            {isOwnerAudience ? (
              <a
                href="https://cal.com/boothdemo/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("bookDemo")}
              </a>
            ) : (
              <Link
                href="/hiring"
                className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("jobBoard")}
              </Link>
            )}
            {/* Logo centered; CTA pinned right. Absolute centering keeps
                the wordmark dead-center regardless of the CTA width. */}
            <Link
              href="/"
              aria-label={t("home")}
              className="absolute left-1/2 -translate-x-1/2 inline-flex items-center"
            >
              {/* Black-text wordmark, hardcoded (not theme-swapped): this bar
                  is pinned white, so the logo must never flip to the white
                  variant — that rendered invisible for dark-mode devices. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/booth-wordmark-light.webp" alt="BOOTH" className="h-6 w-auto" />
            </Link>
            {!isLoading &&
              (isLoggedIn ? (
                <Link
                  href={dashboardUrl}
                  className="inline-flex items-center justify-center h-8 px-4 text-xs font-medium rounded-full bg-black text-white hover:bg-zinc-900 transition-colors"
                >
                  {t("dashboard")}
                </Link>
              ) : (
                <Link
                  href="/auth/signup"
                  className="inline-flex items-center justify-center h-8 px-4 text-xs font-medium rounded-full bg-black text-white hover:bg-zinc-900 transition-colors"
                >
                  {t("signUp")}
                </Link>
              ))}
          </div>
        </header>
      )}

    <header
      className={`hidden md:block fixed top-0 z-50 w-full transition-all duration-300 ease-out ${
        isLanding
          ? `bg-transparent ${isHidden ? "-translate-y-full opacity-0" : "translate-y-0 opacity-100"}`
          : isTokenSurface
            ? "bg-background border-b border-border"
            : "bg-white border-b border-gray-200"
      }`}
    >
      <div className="flex h-14 items-center justify-between md:px-6 lg:px-8 px-1.5">
        {/* Left: Menu Button */}
        <div className="flex-shrink-0 min-w-[44px]">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`rounded-full p-2 transition-opacity hover:opacity-70 ${iconColor}`}
          >
            {isMenuOpen ? <CloseIcon /> : <MoreHorizontalIcon />}
            <span className="sr-only">{t("menu")}</span>
          </button>
        </div>

        {/* Center: Logo — inlined SVG so the ∞ icon stays gray while the
            BOOTH wordmark adapts to theme via currentColor. */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <Link
            href="/"
            aria-label="BOOTH"
            className={`flex-shrink-0 block h-11 md:h-9 transition-colors duration-300 ${
              // Landing: section-aware — the wordmark reads the data-nav-theme
              // of the section behind it (white over dark, black over light),
              // with no drop-shadow. Token surfaces follow app dark mode;
              // marketing pages stay black.
              isDark ? "text-white" : isTokenSurface ? "text-black dark:text-white" : "text-black"
            }`}
          >
            <svg viewBox="0 0 419 121" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-auto">
              <path d="M49.7075 57.6C49.7075 62.0267 49.2808 66.08 48.4275 69.76C47.5742 73.3867 46.3475 76.5067 44.7475 79.12C43.1475 81.7333 41.2008 83.76 38.9075 85.2C36.6142 86.64 34.0008 87.36 31.0675 87.36C28.1342 87.36 25.4942 86.64 23.1475 85.2C20.8542 83.76 18.8808 81.7333 17.2275 79.12C15.6275 76.5067 14.4008 73.3867 13.5475 69.76C12.6942 66.08 12.2675 62.0267 12.2675 57.6C12.2675 53.2267 12.6675 49.2267 13.4675 45.6C14.3208 41.92 15.5475 38.8 17.1475 36.24C18.7475 33.6267 20.6942 31.6 22.9875 30.16C25.3342 28.72 28.0275 28 31.0675 28C34.0008 28 36.6142 28.72 38.9075 30.16C41.2008 31.6 43.1475 33.6267 44.7475 36.24C46.3475 38.8 47.5742 41.92 48.4275 45.6C49.2808 49.2267 49.7075 53.2267 49.7075 57.6ZM43.8675 57.6C43.8675 54.1867 43.6008 50.9867 43.0675 48C42.5342 45.0133 41.7075 42.4267 40.5875 40.24C39.5208 38.0533 38.1875 36.32 36.5875 35.04C34.9875 33.76 33.1475 33.12 31.0675 33.12C28.8808 33.12 26.9608 33.76 25.3075 35.04C23.7075 36.32 22.3742 38.0533 21.3075 40.24C20.2408 42.4267 19.4408 45.0133 18.9075 48C18.3742 50.9867 18.1075 54.1867 18.1075 57.6C18.1075 61.0133 18.3742 64.2133 18.9075 67.2C19.4408 70.1867 20.2408 72.8 21.3075 75.04C22.3742 77.28 23.7075 79.04 25.3075 80.32C26.9608 81.6 28.8808 82.24 31.0675 82.24C33.2008 82.24 35.0675 81.6 36.6675 80.32C38.2675 79.04 39.6008 77.28 40.6675 75.04C41.7342 72.8 42.5342 70.1867 43.0675 67.2C43.6008 64.2133 43.8675 61.0133 43.8675 57.6Z" fill="#696969"/>
              <path d="M129.114 86V27.8182H151.386C155.591 27.8182 159.085 28.4811 161.869 29.8068C164.672 31.1136 166.765 32.9034 168.148 35.1761C169.549 37.4489 170.25 40.0246 170.25 42.9034C170.25 45.2708 169.795 47.2973 168.886 48.983C167.977 50.6496 166.756 52.0038 165.222 53.0455C163.688 54.0871 161.973 54.8352 160.08 55.2898V55.858C162.144 55.9716 164.123 56.6061 166.017 57.7614C167.93 58.8977 169.492 60.5076 170.705 62.5909C171.917 64.6742 172.523 67.1932 172.523 70.1477C172.523 73.1591 171.794 75.8674 170.335 78.2727C168.877 80.6591 166.68 82.5436 163.744 83.9261C160.809 85.3087 157.116 86 152.665 86H129.114ZM139.653 77.1932H150.989C154.814 77.1932 157.57 76.464 159.256 75.0057C160.96 73.5284 161.812 71.6345 161.812 69.3239C161.812 67.6004 161.386 66.0473 160.534 64.6648C159.682 63.2633 158.47 62.1648 156.898 61.3693C155.326 60.5549 153.451 60.1477 151.273 60.1477H139.653V77.1932ZM139.653 52.5625H150.08C151.898 52.5625 153.536 52.2311 154.994 51.5682C156.453 50.8864 157.598 49.9299 158.432 48.6989C159.284 47.4489 159.71 45.9716 159.71 44.267C159.71 42.0133 158.915 40.1572 157.324 38.6989C155.752 37.2405 153.413 36.5114 150.307 36.5114H139.653V52.5625ZM233.68 56.9091C233.68 63.178 232.506 68.5473 230.158 73.017C227.828 77.4678 224.646 80.8769 220.612 83.2443C216.597 85.6117 212.042 86.7955 206.947 86.7955C201.853 86.7955 197.288 85.6117 193.254 83.2443C189.239 80.858 186.057 77.4394 183.709 72.9886C181.379 68.5189 180.214 63.1591 180.214 56.9091C180.214 50.6402 181.379 45.2803 183.709 40.8295C186.057 36.3598 189.239 32.9413 193.254 30.5739C197.288 28.2064 201.853 27.0227 206.947 27.0227C212.042 27.0227 216.597 28.2064 220.612 30.5739C224.646 32.9413 227.828 36.3598 230.158 40.8295C232.506 45.2803 233.68 50.6402 233.68 56.9091ZM223.084 56.9091C223.084 52.4962 222.393 48.7746 221.01 45.7443C219.646 42.6951 217.752 40.3939 215.328 38.8409C212.904 37.2689 210.11 36.483 206.947 36.483C203.785 36.483 200.991 37.2689 198.567 38.8409C196.143 40.3939 194.239 42.6951 192.857 45.7443C191.493 48.7746 190.811 52.4962 190.811 56.9091C190.811 61.322 191.493 65.053 192.857 68.1023C194.239 71.1326 196.143 73.4337 198.567 75.0057C200.991 76.5587 203.785 77.3352 206.947 77.3352C210.11 77.3352 212.904 76.5587 215.328 75.0057C217.752 73.4337 219.646 71.1326 221.01 68.1023C222.393 65.053 223.084 61.322 223.084 56.9091ZM295.634 56.9091C295.634 63.178 294.459 68.5473 292.111 73.017C289.781 77.4678 286.599 80.8769 282.565 83.2443C278.55 85.6117 273.995 86.7955 268.901 86.7955C263.806 86.7955 259.241 85.6117 255.207 83.2443C251.192 80.858 248.01 77.4394 245.662 72.9886C243.332 68.5189 242.168 63.1591 242.168 56.9091C242.168 50.6402 243.332 45.2803 245.662 40.8295C248.01 36.3598 251.192 32.9413 255.207 30.5739C259.241 28.2064 263.806 27.0227 268.901 27.0227C273.995 27.0227 278.55 28.2064 282.565 30.5739C286.599 32.9413 289.781 36.3598 292.111 40.8295C294.459 45.2803 295.634 50.6402 295.634 56.9091ZM285.037 56.9091C285.037 52.4962 284.346 48.7746 282.963 45.7443C281.599 42.6951 279.705 40.3939 277.281 38.8409C274.857 37.2689 272.063 36.483 268.901 36.483C265.738 36.483 262.944 37.2689 260.52 38.8409C258.096 40.3939 256.192 42.6951 254.81 45.7443C253.446 48.7746 252.764 52.4962 252.764 56.9091C252.764 61.322 253.446 65.053 254.81 68.1023C256.192 71.1326 258.096 73.4337 260.52 75.0057C262.944 76.5587 265.738 77.3352 268.901 77.3352C272.063 77.3352 274.857 76.5587 277.281 75.0057C279.705 73.4337 281.599 71.1326 282.963 68.1023C284.346 65.053 285.037 61.322 285.037 56.9091ZM300.463 36.6534V27.8182H346.884V36.6534H328.901V86H318.446V36.6534H300.463ZM355.832 86V27.8182H366.372V52.4489H393.332V27.8182H403.901V86H393.332V61.2841H366.372V86H355.832Z" fill="currentColor"/>
              <path d="M96.7806 57.68C96.7806 54.4267 96.2473 51.3333 95.1806 48.4C94.114 45.4133 92.5673 42.8 90.5406 40.56C88.5673 38.32 86.1406 36.5333 83.2606 35.2C80.3806 33.8667 77.1273 33.2 73.5006 33.2C69.874 33.2 66.6206 33.8667 63.7406 35.2C60.8606 36.5333 58.4073 38.32 56.3806 40.56C54.4073 42.8 52.8873 45.4133 51.8206 48.4C50.754 51.3333 50.2206 54.4267 50.2206 57.68C50.2206 60.9333 50.754 64.0533 51.8206 67.04C52.8873 69.9733 54.4073 72.56 56.3806 74.8C58.4073 77.04 60.8606 78.8267 63.7406 80.16C66.6206 81.4933 69.874 82.16 73.5006 82.16C77.1273 82.16 80.3806 81.4933 83.2606 80.16C86.1406 78.8267 88.5673 77.04 90.5406 74.8C92.5673 72.56 94.114 69.9733 95.1806 67.04C96.2473 64.0533 96.7806 60.9333 96.7806 57.68ZM103.021 57.68C103.021 62 102.274 65.9733 100.781 69.6C99.2873 73.2267 97.2073 76.3733 94.5406 79.04C91.9273 81.6533 88.8073 83.7067 85.1806 85.2C81.6073 86.6933 77.714 87.44 73.5006 87.44C69.2873 87.44 65.3673 86.6933 61.7406 85.2C58.1673 83.7067 55.0473 81.6533 52.3806 79.04C49.7673 76.3733 47.714 73.2267 46.2206 69.6C44.7273 65.9733 43.9806 62 43.9806 57.68C43.9806 53.36 44.7273 49.3867 46.2206 45.76C47.714 42.1333 49.7673 39.0133 52.3806 36.4C55.0473 33.7333 58.1673 31.6533 61.7406 30.16C65.3673 28.6667 69.2873 27.92 73.5006 27.92C77.714 27.92 81.6073 28.6667 85.1806 30.16C88.8073 31.6533 91.9273 33.7333 94.5406 36.4C97.2073 39.0133 99.2873 42.1333 100.781 45.76C102.274 49.3867 103.021 53.36 103.021 57.68Z" fill="#696969"/>
            </svg>
          </Link>
        </div>

        {/* Right: Auth Buttons (Desktop) — typography aligned with the
            project design system. Geist Sans (project default), 13px
            with tracking-tight for the nav links so they read as crisp
            secondary chrome rather than primary text. CTA pill uses the
            standard h-8 px-4 text-xs pattern (matches the dashboard
            chair button + signed-in pills) — no gradient/scale-105. */}
        <div className="hidden md:flex items-center gap-1 flex-shrink-0 ml-auto">
          {/* Listings is barber territory browsing shops in one market —
              a language switcher next to it reads as clutter, not a feature. */}
          {!isListingsSurface && <LocaleSwitcher className={`mr-2 ${linkClasses}`} />}
          {!isLoading &&
            (isLoggedIn ? (
              <>
                <Link
                  href={primaryLink.href}
                  className={`px-2.5 py-1.5 text-[13px] font-medium tracking-tight transition-colors ${linkClasses}`}
                >
                  {primaryLink.label}
                </Link>
                <Link
                  href="/community"
                  className={`px-2.5 py-1.5 text-[13px] font-medium tracking-tight transition-colors ${linkClasses}`}
                >
                  {t("community")}
                </Link>
                <Link
                  href={dashboardUrl}
                  className={`ml-1 inline-flex items-center justify-center h-8 px-4 text-xs font-medium rounded-full transition-colors ${ctaClasses}`}
                >
                  {t("dashboard")}
                </Link>
              </>
            ) : (
              <>
                {/* Booking surfaces hide the pro primary link (Browse
                    Shops / Find Barbers) — the visitor is here to book. */}
                {!bookingSurface && (
                  <Link
                    href={primaryLink.href}
                    className={`px-2.5 py-1.5 text-[13px] font-medium tracking-tight transition-colors ${linkClasses}`}
                  >
                    {primaryLink.label}
                  </Link>
                )}
                {/* Community is in-app only — no public link for logged-out visitors. */}
                <Link
                  href={signupHref}
                  className={`ml-1 inline-flex items-center justify-center h-8 px-4 text-xs font-medium rounded-full transition-colors ${ctaClasses}`}
                >
                  {t("signUp")}
                </Link>
              </>
            ))}
        </div>

        {/* Right: Auth Button (Mobile) */}
        <div className="md:hidden flex-shrink-0 min-w-[44px] flex justify-end ml-auto">
          {!isLoading && !isLoggedIn && (
            <Link
              href={signupHref}
              className="group relative overflow-hidden px-3.5 py-1.5 text-xs font-medium bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white rounded-full border border-white/10 hover:shadow-white/20 hover:scale-105 transition-all duration-300"
            >
              <span className="relative z-10">{t("signUp")}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </Link>
          )}
          {(isLoading || isLoggedIn) && <div className="w-10" />}
        </div>
      </div>

      {isMenuOpen && (
        <>
          {/* Click-outside catcher (md+) — invisible, no dim. The
              modal flyout uses the same pattern as DesktopFloatingNav
              so the public surface visually rhymes with the auth
              chrome menu, just white-themed for marketing. */}
          <div
            className="hidden md:block fixed inset-0 top-14 z-40"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Desktop modal flyout — black, mirrors the floating-nav
              modal style. 3-col tile grid + auth pill at the bottom.
              Sign-out icon removed per design — Sign Out lives on the
              account page. Positioned clearly below the navbar (top-[4.5rem]
              = 72px, navbar is 56px) and inset from the left edge so it
              reads as a flyout from the trigger rather than a banner. */}
          <div
            className="hidden md:block absolute top-[4.5rem] left-4 z-50 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.22)] animate-in zoom-in-95 fade-in slide-in-from-top-1 duration-300 ease-out origin-top-left"
            style={{ width: 340, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}
          >
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              aria-label={t("closeMenu")}
              className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 transition-colors z-10"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>

            {/* Tile grid — naked icon + label, 3-col layout. The 6th
                cell (bottom-right) is the Sign Up / Dashboard CTA,
                styled as a white pill so it reads as a button rather
                than another nav link. */}
            <div className="px-5 pt-5 pb-4">
              {/* Booking surfaces carry just Pricing + the Sign Up CTA —
                  a 2-col grid centers the pair instead of leaving a dead
                  third column. */}
              <div className={`grid gap-y-4 gap-x-2 ${bookingSurface ? "grid-cols-2" : "grid-cols-3"}`}>
                {publicMenuItems.map((item, i) => {
                  const Icon = item.icon
                  const active = item.href === pathname
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={`group flex flex-col items-center justify-center gap-1.5 hover:scale-[1.06] focus-visible:outline-none transition-transform ${
                        active ? "text-white" : "text-white/70 hover:text-white"
                      }`}
                      style={{
                        animation: `pubNavTileIn 380ms cubic-bezier(0.22, 1, 0.36, 1) ${80 + i * 55}ms backwards`,
                      }}
                    >
                      <Icon className="w-6 h-6" strokeWidth={1.5} />
                      <span className="text-[12px] font-medium tracking-wide">{item.label}</span>
                    </Link>
                  )
                })}
                {!isLoading && (isLoggedIn ? (
                  <Link
                    href={dashboardUrl}
                    onClick={() => setIsMenuOpen(false)}
                    className="group flex flex-col items-center justify-center gap-1.5 hover:scale-[1.06] focus-visible:outline-none transition-transform text-white"
                    style={{
                      animation: `pubNavTileIn 420ms cubic-bezier(0.22, 1, 0.36, 1) ${80 + publicMenuItems.length * 55}ms backwards`,
                    }}
                  >
                    <LayoutDashboard className="w-6 h-6" strokeWidth={1.5} />
                    <span className="text-[10px] font-medium tracking-wide bg-white text-black px-2.5 py-1 rounded-full whitespace-nowrap">{t("dashboard")}</span>
                  </Link>
                ) : (
                  <Link
                    href={signupHref}
                    onClick={() => setIsMenuOpen(false)}
                    className="group flex flex-col items-center justify-center gap-1.5 hover:scale-[1.06] focus-visible:outline-none transition-transform text-white"
                    style={{
                      animation: `pubNavTileIn 420ms cubic-bezier(0.22, 1, 0.36, 1) ${80 + publicMenuItems.length * 55}ms backwards`,
                    }}
                  >
                    <UserPlus className="w-6 h-6" strokeWidth={1.5} />
                    <span className="text-[10px] font-medium tracking-wide bg-white text-black px-2.5 py-1 rounded-full whitespace-nowrap">{t("signUp")}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile menu — vertical sheet for sub-md viewports. The
              desktop modal pattern doesn't translate to narrow widths
              so mobile keeps the slide-in-from-top sheet. */}
          <div className="md:hidden fixed inset-0 top-14 bg-black/20 z-40" onClick={() => setIsMenuOpen(false)} />
          <div className="md:hidden absolute left-0 right-0 top-14 bg-white border-b border-gray-200 shadow-lg z-50 animate-in slide-in-from-top-2 duration-200">
            <div className="p-4 space-y-1 max-w-md">
              {!isLoading &&
                (isLoggedIn ? (
                  <>
                    <Link
                      href={dashboardUrl}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <LayoutDashboard className="h-5 w-5" />
                      <span>{t("dashboard")}</span>
                    </Link>
                    <div className="border-b border-gray-200 my-2" />
                  </>
                ) : (
                  <>
                    <Link
                      href="/auth/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <LogIn className="h-5 w-5" />
                      <span>{t("logIn")}</span>
                    </Link>
                    <Link
                      href={signupHref}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <UserPlus className="h-5 w-5" />
                      <span>{t("signUp")}</span>
                    </Link>
                    <div className="border-b border-gray-200 my-2" />
                  </>
                ))}

              {publicMenuItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-colors"
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}

              <div className="border-t border-gray-200 mt-2 pt-3 px-4">
                <LocaleSwitcher className="text-gray-700" />
              </div>

              {isLoggedIn && (
                <>
                  <div className="border-t border-gray-200 mt-2 pt-2" />
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>{t("signOut")}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <style jsx>{`
            @keyframes pubNavTileIn {
              from { opacity: 0; transform: translateY(10px) scale(0.94); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
        </>
      )}

      <style>{`
        body {
          padding-top: ${isLanding || isBookingFunnelSelfPadded ? "0" : "3.5rem"};
        }
      `}</style>
    </header>
    </>
  )
}
